import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Modal from './Modal';
import Card from './Card';

const REVEAL_STAGGER_MS = 1200;
const INITIAL_DELAY_MS = 600;
const SPARKLE_COUNT = 64;

const CONFETTI_COLORS = ['#17CA07', '#FFD700', '#00CED1', '#FF6B6B', '#E879F9', '#FB923C', '#C0C0C0', '#FFFFFF'];

const generateConfetti = (count) =>
    Array.from({ length: count }, (_, index) => {
        const angle = (Math.PI * 2 * index) / count + (Math.random() - 0.5) * 0.5;
        const distance = 120 + Math.random() * 200;
        const endX = Math.cos(angle) * distance;
        const endY = Math.sin(angle) * distance - 40;
        const rotation = Math.random() * 720 - 360;
        return {
            id: index,
            width: 4 + Math.random() * 6,
            height: 8 + Math.random() * 12,
            color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
            delay: Math.random() * 0.3,
            duration: 0.8 + Math.random() * 0.6,
            endX,
            endY,
            rotation,
        };
    });

const ConfettiBurst = () => {
    const confetti = useMemo(() => generateConfetti(SPARKLE_COUNT), []);

    return (
        <div className="trade-up-results__confetti">
            {confetti.map(piece => (
                <span
                    key={piece.id}
                    className="trade-up-results__confetti-piece"
                    style={{
                        width: piece.width,
                        height: piece.height,
                        backgroundColor: piece.color,
                        animationDelay: `${piece.delay}s`,
                        animationDuration: `${piece.duration}s`,
                        '--end-x': `${piece.endX}px`,
                        '--end-y': `${piece.endY}px`,
                        '--rotation': `${piece.rotation}deg`,
                    }}
                />
            ))}
        </div>
    );
};

const TradeUpResultsModal = ({ results, onClose, onTradeAgain }) => {
    const [revealedCount, setRevealedCount] = useState(0);
    const [showSparkles, setShowSparkles] = useState(false);

    useEffect(() => {
        if (revealedCount >= results.length) return;

        const delay = revealedCount === 0 ? INITIAL_DELAY_MS : REVEAL_STAGGER_MS;
        const timer = setTimeout(() => setRevealedCount(prev => prev + 1), delay);
        return () => clearTimeout(timer);
    }, [revealedCount, results.length]);

    useEffect(() => {
        if (revealedCount >= results.length) setShowSparkles(true);
    }, [revealedCount, results.length]);

    const revealAll = useCallback(() => {
        setRevealedCount(results.length);
    }, [results.length]);

    const allRevealed = revealedCount >= results.length;

    return (
        <Modal onClose={onClose} title="Trade-Up Results">
            <div className="trade-up-results">
                {showSparkles && <ConfettiBurst />}

                <h3 className="trade-up-results__title">
                    {results.length} card{results.length > 1 ? 's' : ''} upgraded!
                </h3>

                <div className="trade-up-results__cards">
                    {results.map((tradeResult, index) => (
                        <div
                            key={tradeResult.inventoryID || index}
                            className={`trade-up-results__card ${index < revealedCount ? 'trade-up-results__card--revealed' : ''}`}
                        >
                            <Card
                                card={tradeResult.Card}
                                variation={tradeResult.CardVariation}
                                showLabel
                            />
                        </div>
                    ))}
                </div>

                <div className="trade-up-results__actions">
                    {!allRevealed ? (
                        <button onClick={revealAll}>Reveal All</button>
                    ) : (
                        <>
                            <button onClick={onClose}>Done</button>
                            {onTradeAgain && (
                                <button onClick={onTradeAgain}>Trade Again</button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </Modal>
    );
};

TradeUpResultsModal.propTypes = {
    results: PropTypes.arrayOf(PropTypes.shape({
        inventoryID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        Card: PropTypes.object,
        CardVariation: PropTypes.shape({
            name: PropTypes.string,
            cssClass: PropTypes.string,
        }),
    })).isRequired,
    onClose: PropTypes.func.isRequired,
    onTradeAgain: PropTypes.func,
};

export default TradeUpResultsModal;
